import { useEffect } from 'react';
import { useFieldArray, useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import type { CreateGoalInput } from '@pms/shared';
import { createGoalSchema, PERMISSIONS } from '@pms/shared';
import { goalsService } from '@/services/goals.service';
import { usersService } from '@/services/users.service';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { GoalAssigneeSelect } from './GoalAssigneeSelect';
import { getErrorMessage } from '@/lib/errors';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ALL_GOAL_PRIORITIES,
  ALL_GOAL_TARGET_TYPES,
  GOAL_PRIORITY_META,
  GOAL_TARGET_TYPE_META,
  NONE_OPTION,
} from '@/components/goals/goalConstants';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Pre-selects the owner; the assignee picker is hidden when set. */
  lockedUserId?: string;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Empty numeric inputs must become null, not the 0 that `z.coerce.number()` yields. */
const numberOrNull = { setValueAs: (v: unknown) => (v === '' || v === null || v === undefined ? null : Number(v)) };

const DEFAULTS: CreateGoalInput = {
  user_ids: [],
  category_id: null,
  title: '',
  description: null,
  target_type: 'numeric',
  start_value: 0,
  target_value: null,
  unit: null,
  direction: 'increase',
  weight: 100,
  priority: 'medium',
  start_date: today(),
  due_date: '',
  status: 'active',
  milestones: [],
};

export function CreateGoalModal({ open, onClose, lockedUserId }: Props) {
  const queryClient = useQueryClient();
  const perms = usePermission();
  const { user } = useAuth();
  const currentUserId = user?.id ?? '';
  const canAssign = perms.has(PERMISSIONS.GOALS_ASSIGN) && !lockedUserId;

  const { data: categories = [] } = useQuery({
    queryKey: ['goals', 'categories'],
    queryFn: () => goalsService.listCategories(),
    enabled: open,
  });

  const { data: people = [] } = useQuery({
    queryKey: ['users', 'picker'],
    queryFn: () => usersService.picker(),
    enabled: open && canAssign,
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateGoalInput>({
    resolver: zodResolver(createGoalSchema),
    defaultValues: { ...DEFAULTS, user_ids: lockedUserId ? [lockedUserId] : [] },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'milestones' });

  const targetType = watch('target_type');
  const startDate = watch('start_date');
  const isMilestone = targetType === 'milestone';

  // Switching target type clears the fields that no longer apply so a stale
  // numeric target can never be submitted with a milestone goal.
  useEffect(() => {
    if (isMilestone) {
      setValue('target_value', null);
      setValue('start_value', 0);
      setValue('unit', null);
      setValue('direction', 'increase');
      if (fields.length === 0) append({ title: '' });
    } else {
      setValue('milestones', []);
      if (targetType === 'percentage') setValue('unit', null);
    }
    // `fields.length`/`append` are intentionally excluded: this should react to the
    // target-type choice only, not to every checklist edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetType, isMilestone, setValue]);

  const mutation = useMutation({
    mutationFn: (data: CreateGoalInput) => goalsService.createGoal(data),
    onSuccess: ({ ids }) => {
      toast.success(ids.length > 1 ? `Goal created for ${ids.length} employees` : 'Goal created');
      void queryClient.invalidateQueries({ queryKey: ['goals'] });
      handleClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  function handleClose() {
    reset({ ...DEFAULTS, user_ids: lockedUserId ? [lockedUserId] : [] });
    onClose();
  }

  const busy = isSubmitting || mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Goal</DialogTitle>
          <DialogDescription>
            Define a measurable target and a deadline so progress can be tracked.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit((data) => mutation.mutate(data))} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="goal-title">
              Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="goal-title"
              placeholder="e.g. Reach 1M Instagram followers"
              className={errors.title ? 'border-destructive' : ''}
              {...register('title')}
            />
            {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
          </div>

          <div className="space-y-1">
            <Label htmlFor="goal-description">Description</Label>
            <Textarea
              id="goal-description"
              rows={3}
              placeholder="What does success look like, and how will it be measured?"
              {...register('description')}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {canAssign && (
              <div className="space-y-1">
                <Label>Assign to</Label>
                <Controller
                  control={control}
                  name="user_ids"
                  render={({ field }) => (
                    <GoalAssigneeSelect
                      value={field.value ?? []}
                      onChange={field.onChange}
                      people={people}
                      currentUserId={currentUserId}
                    />
                  )}
                />
                {errors.user_ids && <p className="text-xs text-destructive">{errors.user_ids.message}</p>}
                <p className="text-[11px] text-muted-foreground">
                  Pick several to give each person their own copy of this goal.
                </p>
              </div>
            )}

            <div className="space-y-1">
              <Label>Category</Label>
              <Controller
                control={control}
                name="category_id"
                render={({ field }) => (
                  <Select
                    value={field.value ?? NONE_OPTION}
                    onValueChange={(v) => field.onChange(v === NONE_OPTION ? null : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Uncategorized" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_OPTION}>Uncategorized</SelectItem>
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="space-y-1">
              <Label>
                Target type <span className="text-destructive">*</span>
              </Label>
              <Controller
                control={control}
                name="target_type"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ALL_GOAL_TARGET_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>{GOAL_TARGET_TYPE_META[t].label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              <p className="text-[11px] text-muted-foreground">{GOAL_TARGET_TYPE_META[targetType].hint}</p>
            </div>

            <div className="space-y-1">
              <Label>Priority</Label>
              <Controller
                control={control}
                name="priority"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ALL_GOAL_PRIORITIES.map((p) => (
                        <SelectItem key={p} value={p}>{GOAL_PRIORITY_META[p].label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          {!isMilestone && (
            <div className="grid grid-cols-1 gap-4 rounded-lg border border-border/60 bg-muted/20 p-3 sm:grid-cols-2">
              <div className="space-y-1 sm:col-span-2">
                <Label>Direction</Label>
                <Controller
                  control={control}
                  name="direction"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="increase">Increase — grow towards the target</SelectItem>
                        <SelectItem value="decrease">Decrease — bring the number down</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="goal-start-value">Starting value</Label>
                <Input
                  id="goal-start-value"
                  type="number"
                  step="any"
                  placeholder="0"
                  className={errors.start_value ? 'border-destructive' : ''}
                  {...register('start_value', {
                    setValueAs: (v: unknown) => (v === '' || v === null || v === undefined ? 0 : Number(v)),
                  })}
                />
                {errors.start_value && <p className="text-xs text-destructive">{errors.start_value.message}</p>}
              </div>

              <div className="space-y-1">
                <Label htmlFor="goal-target-value">
                  Target value <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="goal-target-value"
                  type="number"
                  step="any"
                  placeholder={targetType === 'percentage' ? '100' : 'e.g. 1000000'}
                  className={errors.target_value ? 'border-destructive' : ''}
                  {...register('target_value', numberOrNull)}
                />
                {errors.target_value && <p className="text-xs text-destructive">{errors.target_value.message}</p>}
              </div>

              {(targetType === 'numeric') && (
                <div className="space-y-1 sm:col-span-2">
                  <Label htmlFor="goal-unit">Unit</Label>
                  <Input id="goal-unit" placeholder="e.g. followers, calls, articles" {...register('unit')} />
                </div>
              )}
            </div>
          )}

          {isMilestone && (
            <div className="space-y-2 rounded-lg border border-border/60 bg-muted/20 p-3">
              <div className="flex items-center justify-between gap-2">
                <Label>
                  Milestones <span className="text-destructive">*</span>
                </Label>
                <Button type="button" variant="outline" size="sm" onClick={() => append({ title: '' })}>
                  <Plus className="h-3.5 w-3.5" /> Add
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Progress is the share of ticked items — good for shipping a project in stages.
              </p>
              <div className="space-y-2">
                {fields.map((f, idx) => (
                  <div key={f.id} className="flex items-center gap-2">
                    <Input
                      placeholder={`Milestone ${idx + 1}`}
                      className={errors.milestones?.[idx]?.title ? 'border-destructive' : ''}
                      {...register(`milestones.${idx}.title` as const)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Remove milestone"
                      disabled={fields.length <= 1}
                      onClick={() => remove(idx)}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                ))}
              </div>
              {errors.milestones && typeof errors.milestones.message === 'string' && (
                <p className="text-xs text-destructive">{errors.milestones.message}</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>
                Start date <span className="text-destructive">*</span>
              </Label>
              <Controller
                control={control}
                name="start_date"
                render={({ field }) => (
                  <DatePicker value={field.value ?? ''} onChange={field.onChange} placeholder="Start date" />
                )}
              />
              {errors.start_date && <p className="text-xs text-destructive">{errors.start_date.message}</p>}
            </div>

            <div className="space-y-1">
              <Label>
                Due date <span className="text-destructive">*</span>
              </Label>
              <Controller
                control={control}
                name="due_date"
                render={({ field }) => (
                  <DatePicker
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    placeholder="Due date"
                    minDate={startDate ? new Date(`${startDate}T00:00:00`) : undefined}
                  />
                )}
              />
              {errors.due_date && <p className="text-xs text-destructive">{errors.due_date.message}</p>}
            </div>

            <div className="space-y-1">
              <Label htmlFor="goal-weight">Weight</Label>
              <Input
                id="goal-weight"
                type="number"
                min={1}
                max={100}
                {...register('weight', {
                  setValueAs: (v: unknown) => (v === '' || v === null || v === undefined ? 100 : Number(v)),
                })}
              />
              {errors.weight
                ? <p className="text-xs text-destructive">{errors.weight.message}</p>
                : <p className="text-[11px] text-muted-foreground">How much this goal counts, 1–100.</p>}
            </div>

            <div className="space-y-1">
              <Label>Start as</Label>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active — start tracking now</SelectItem>
                      <SelectItem value="draft">Draft — plan it for later</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Goal
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
